import {useState, useCallback} from 'react';
import uuid from 'src/nativeModules/UUID';
import {initStripe, createPaymentMethod} from 'src/nativeModules/Stripe';
import ActionsFactory from 'src/actions/ActionsFactory';
import AccountStore from 'src/stores/AccountStore';
import Settings from 'src/Settings';
import Localized from 'src/constants/AppStrings';
import {alertError} from 'src/components/helpers/AlertHelper';
import Logger from 'src/logging/Logger';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import type {PaymentCredentials} from 'src/models/PaymentCredentials';
import type {CCModel} from 'src/types/CCModel';

interface UseStripeCardSetupProps {
  paymentCredentials: PaymentCredentials;
  onComplete: () => void;
}

export const useStripeCardSetup = ({
  paymentCredentials,
  onComplete,
}: UseStripeCardSetupProps) => {
  const [loading, setLoading] = useState(false);

  const createPaymentToken = useCallback(async () => {
    try {
      const {paymentMethod, error} = await createPaymentMethod({
        paymentMethodType: 'Card',
      });

      if (error) {
        alertError(Localized.Errors.error_adding_credit_card, error.message);
        return null;
      }

      return paymentMethod;
    } catch (error) {
      return null;
    }
  }, []);

  const addCardWithStripe = useCallback(async () => {
    setLoading(true);

    try {
      await initStripe({publishableKey: paymentCredentials?.key});

      const response =
        await ActionsFactory.getAccountActions().createSetupIntent(
          AccountStore.getAccountId(),
        );

      Logger.Log.LogAPIEvent(
        'AppAPI',
        'CreateSetupIntent',
        JSON.stringify({accountId: AccountStore.getAccountId()}),
        JSON.stringify(response),
      );

      if (!response?.clientSecret) {
        alertError(Localized.Errors.error_adding_credit_card);
        setLoading(false);
        return;
      }

      const token = await createPaymentToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const ccModel: CCModel = {
        CardNumberMask: `x${token.Card.last4}`,
        Token: token.id,
        Provider: Settings.processors.stripe,
        AccountId: AccountStore.getAccountId(),
        BillingZip: token.billingDetails.address.postalCode,
        CardNumber: '',
        CvvCode: '',
        ExpirationMonth: token.Card.expMonth,
        ExpirationYear: token.Card.expYear,
        Issuer: token.Card.brand,
      };

      await ActionsFactory.getAccountActions().addCreditCardTokenStripe(
        ccModel,
      );
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      const errorMessage = generateErrorMessage(error);

      CrashlyticsEvents.log(
        'Exception',
        'CreditCardsScreen:AddCardStripe',
        errorMessage,
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'CreditCardsScreen:AddCardStripe',
        errorMessage,
        guid,
      );

      if (error?.message?.includes('CreateSetupIntent')) {
        Logger.Log.LogAPIEvent(
          'AppAPI',
          'CreateSetupIntent',
          JSON.stringify({accountId: AccountStore.getAccountId()}),
          JSON.stringify(error),
        );
      }

      alertError(Localized.Errors.error_adding_credit_card, guid);
    } finally {
      setLoading(false);
      onComplete();
    }
  }, [paymentCredentials, createPaymentToken, onComplete]);

  return {loading, addCardWithStripe};
};
